import { Link } from 'react-router-dom';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

const SERVICES = [
  {
    id: 'commercial-cleaning',
    name: 'Commercial Cleaning',
    icon: 'ri-building-line',
    description: 'Commercial-grade cleaning that meets the highest standards. Keep your facility spotless, compliant, and ready to impress every visitor.',
    features: ['Routine & deep cleaning', 'EPA-approved products', 'Flexible day/night scheduling'],
    slug: '/services/commercial-cleaning',
    image: 'https://public.readdy.ai/ai/img_res/f7de470a0602bd6d452c9d84aaf0d196.jpg',
    badge: 'Most Popular',
  },
  {
    id: 'janitorial',
    name: 'Janitorial Services',
    icon: 'ri-brush-line',
    description: 'Consistent, reliable janitorial maintenance that keeps your facility clean and presentable every single day, around your schedule.',
    features: ['Daily upkeep', 'Trash & recycling removal', 'Supply restocking'],
    slug: '/services/janitorial',
    image: 'https://readdy.ai/api/search-image?query=clean%20bright%20modern%20corporate%20office%20hallway%20corridor%20with%20sparkling%20floors%20white%20walls%20professional%20business%20environment%20luxury%20interior%20minimalist%20design%20pristine%20no%20people&width=800&height=500&seq=prime-svc-jan-1&orientation=landscape',
  },
  {
    id: 'office-cleaning',
    name: 'Office Cleaning',
    icon: 'ri-computer-line',
    description: 'Office cleaning that creates a productive, healthy work environment and projects professionalism to every client who walks in.',
    features: ['Desk & workstation care', 'Conference room upkeep', 'Break room sanitation'],
    slug: '/services/office-cleaning',
    image: 'https://readdy.ai/api/search-image?query=modern%20open%20plan%20office%20workspace%20with%20clean%20organized%20desks%20computers%20bright%20natural%20lighting%20plants%20minimalist%20professional%20corporate%20interior%20spotless%20polished%20floors%20no%20people&width=800&height=500&seq=prime-svc-off-1&orientation=landscape',
  },
  {
    id: 'floor-care',
    name: 'Floor Care & Maintenance',
    icon: 'ri-layout-bottom-line',
    description: 'Expert floor care services including stripping, waxing, polishing, and restoration that extend the life of your flooring investment.',
    features: ['Stripping & waxing', 'Buffing & high-speed polishing', 'Hard floor restoration'],
    slug: '/services/floor-care',
    image: 'https://readdy.ai/api/search-image?query=highly%20polished%20reflective%20marble%20tile%20floor%20in%20luxury%20commercial%20building%20interior%20gleaming%20surface%20elegant%20architectural%20space%20professional%20cleaning%20result%20pristine%20high%20gloss%20no%20people&width=800&height=500&seq=prime-svc-floor-1&orientation=landscape',
  },
  {
    id: 'post-construction',
    name: 'Post-Construction Cleaning',
    icon: 'ri-building-2-line',
    description: 'Thorough post-construction cleanup that removes all debris, dust, and residue — preparing your new space for a flawless grand opening.',
    features: ['Complete debris removal', 'Construction dust elimination', 'Final detail inspection & polish'],
    slug: '/services/post-construction',
    image: 'https://readdy.ai/api/search-image?query=newly%20completed%20modern%20commercial%20building%20interior%20freshly%20cleaned%20bright%20open%20space%20new%20construction%20finished%20architecture%20clean%20ready%20for%20occupancy%20professional%20interior%20design%20no%20people&width=800&height=500&seq=prime-svc-post-1&orientation=landscape',
  },
  {
    id: 'specialized',
    name: 'Specialized Facility Cleaning',
    icon: 'ri-sparkling-line',
    description: 'Custom cleaning solutions engineered for specialized environments including healthcare, hospitality, industrial, and high-security facilities.',
    features: ['Healthcare facility compliance', 'Hospitality & hotel cleaning', 'Industrial & warehouse spaces'],
    slug: '/services/specialized',
    image: 'https://readdy.ai/api/search-image?query=pristine%20modern%20healthcare%20medical%20facility%20corridor%20bright%20clinical%20environment%20spotless%20white%20floors%20sterile%20professional%20medical%20building%20interior%20clean%20hygienic%20no%20people&width=800&height=500&seq=prime-svc-spec-1&orientation=landscape',
  },
];

export default function ServicesOverview() {
  const { ref, isVisible } = useScrollAnimation();

  return (
    <section className="py-24 bg-[#FAF8F5]">
      <div className="max-w-7xl mx-auto px-6">
        <div ref={ref} className={`text-center mb-16 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <span className="inline-block px-5 py-2 bg-[#C70532]/10 text-[#C70532] text-xs font-bold uppercase tracking-widest rounded-full mb-5">What We Offer</span>
          <h2 className="text-4xl md:text-5xl font-black text-[#03143A] mb-5 leading-tight">Professional Facility Services</h2>
          <p className="text-lg text-gray-500 max-w-2xl mx-auto leading-relaxed">Comprehensive cleaning and facility management solutions tailored to every type of commercial property — from offices to hospitals.</p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-7">
          {SERVICES.map((service, i) => (
            <div
              key={service.id}
              className={`group bg-white rounded-3xl border border-gray-100 overflow-hidden hover:border-[#C70532]/20 transition-all duration-500 flex flex-col ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{ transitionDelay: `${i * 70}ms` }}
            >
              {/* Image */}
              <div className="h-52 overflow-hidden relative flex-shrink-0">
                <img
                  src={service.image}
                  alt={`${service.name} Houston TX`}
                  className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
                {service.badge && (
                  <span className="absolute top-4 left-4 px-3 py-1 bg-[#C70532] text-white text-xs font-bold rounded-full tracking-wide">
                    {service.badge}
                  </span>
                )}
                <div className="absolute bottom-4 left-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-white/20 backdrop-blur-sm rounded-xl border border-white/30">
                    <i className={`${service.icon} text-white text-lg`} />
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-7 flex flex-col flex-1">
                <h3 className="font-black text-[#03143A] text-xl mb-2.5 group-hover:text-[#C70532] transition-colors duration-300 leading-tight">{service.name}</h3>
                <p className="text-gray-500 text-sm mb-5 leading-relaxed flex-1">{service.description}</p>
                <ul className="space-y-2 mb-6">
                  {service.features.map((f) => (
                    <li key={f} className="flex items-center gap-2.5 text-xs text-gray-500">
                      <div className="w-5 h-5 flex items-center justify-center bg-[#C70532]/10 rounded-full flex-shrink-0">
                        <i className="ri-check-line text-[#C70532] text-xs" />
                      </div>
                      {f}
                    </li>
                  ))}
                </ul>
                <Link
                  to={service.slug}
                  className="inline-flex items-center gap-2 text-[#C70532] text-sm font-bold hover:gap-3 transition-all duration-300 cursor-pointer group/link"
                >
                  Learn More
                  <div className="w-5 h-5 flex items-center justify-center bg-[#C70532]/10 rounded-full group-hover/link:bg-[#C70532] transition-colors">
                    <i className="ri-arrow-right-line text-[#C70532] text-xs group-hover/link:text-white transition-colors" />
                  </div>
                </Link>
              </div>
            </div>
          ))}
        </div>

        {/* Services CTA block */}
        <div className={`mt-14 bg-[#03143A] rounded-3xl p-10 flex flex-col md:flex-row items-center justify-between gap-6 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{ transitionDelay: '450ms' }}>
          <div>
            <p className="text-white/60 text-sm font-semibold uppercase tracking-widest mb-1">Not sure what you need?</p>
            <h3 className="text-2xl font-bold text-white">Get a Custom Facility Services Plan Today</h3>
            <p className="text-white/60 text-sm mt-1">Free assessment &bull; No obligation &bull; Tailored to your budget</p>
          </div>
          <div className="flex items-center gap-4 flex-shrink-0">
            <a href="tel:+17133382553" className="flex items-center gap-2 px-6 py-3 bg-white/10 hover:bg-white/20 text-white font-bold rounded-full border border-white/20 transition-all cursor-pointer whitespace-nowrap">
              <i className="ri-phone-fill text-[#C70532]" />
              (713) 338-2553
            </a>
            <Link to="/contact" className="px-7 py-3 bg-[#C70532] hover:bg-[#a80428] text-white font-bold rounded-full transition-all cursor-pointer whitespace-nowrap">
              Request a Plan
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
